import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import './RelevantServices.css'
import RichTextContent, { CmsText } from '../../../RichTextContent/RichTextContent'
import { useIndustrySection } from '../../../../context/IndustryPageContext'
import { getServiceCardsFromSection } from '../../../../services/contentApi'

import icon1 from '../../../../assets/icons/capabilities/icon1.svg'
import icon2 from '../../../../assets/icons/capabilities/icon2.svg'
import icon3 from '../../../../assets/icons/capabilities/icon3.svg'
import icon4 from '../../../../assets/icons/capabilities/icon4.svg'
import icon5 from '../../../../assets/icons/capabilities/icon5.svg'
import icon6 from '../../../../assets/icons/capabilities/icon6.svg'

const icons = [icon1, icon2, icon3, icon4, icon5, icon6]

const RelevantServices = () => {
  const { section, loading } = useIndustrySection('relevant-services')
  const sliderRef = useRef()
  const isDraggingRef = useRef(false)
  const dragStartXRef = useRef(0)
  const dragStartScrollLeftRef = useRef(0)

  const getScrollStep = () => {
    const slider = sliderRef.current
    if (!slider) return 400

    const firstCard = slider.querySelector(".rel-card")
    if (!firstCard) return Math.max(320, Math.round(slider.clientWidth * 0.8))

    const cardWidth = firstCard.getBoundingClientRect().width
    const sliderStyles = window.getComputedStyle(slider)
    const gap = parseFloat(sliderStyles.columnGap || sliderStyles.gap || "0")
    return Math.round(cardWidth + gap)
  }

  const scrollLeft = () => {
    if (!sliderRef.current) return
    sliderRef.current.scrollBy({ left: -getScrollStep(), behavior: 'smooth' })
  }

  const scrollRight = () => {
    if (!sliderRef.current) return
    sliderRef.current.scrollBy({ left: getScrollStep(), behavior: 'smooth' })
  }

  const handleMouseDown = (e) => {
    const slider = sliderRef.current
    if (!slider) return

    isDraggingRef.current = true
    dragStartXRef.current = e.clientX
    dragStartScrollLeftRef.current = slider.scrollLeft
    slider.classList.add("rel-slider--dragging")
  }

  const handleMouseMove = (e) => {
    const slider = sliderRef.current
    if (!slider || !isDraggingRef.current) return

    e.preventDefault()
    const deltaX = e.clientX - dragStartXRef.current
    slider.scrollLeft = dragStartScrollLeftRef.current - deltaX
  }

  const stopDragging = () => {
    const slider = sliderRef.current
    isDraggingRef.current = false
    if (slider) {
      slider.classList.remove("rel-slider--dragging")
    }
  }

  if (loading || !section?.fields) return null

  const fields = section.fields
  const cards = getServiceCardsFromSection(section)

  return (
    <section className="relevant">
      <div className="relevant-header">
        {fields.subtitle ? (
          <CmsText
            content={fields.subtitle}
            as="p"
            className="rel-subtitle"
          />
        ) : null}

        {fields.title ? (
          <CmsText content={fields.title} as="h2" className="rel-title" />
        ) : null}

        {fields.description ? (
          <RichTextContent content={fields.description} className="rel-desc" />
        ) : null}
      </div>

      <div className="rel-slider-wrapper">
        <div
          className="rel-slider"
          ref={sliderRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={stopDragging}
          onMouseLeave={stopDragging}
          onDragStart={(e) => e.preventDefault()}
        >
          {cards.map((card, i) => (
            <div
              key={i}
              className={`service-card rel-card ${card.highlight ? "highlight" : ""}`}
            >
              <div className="icon">
                <img src={icons[i]} alt="" />
              </div>

              <div>
                <CmsText content={card.category} as="p" className="category" />
                <CmsText content={card.title} as="h3" />
              </div>

              <RichTextContent content={card.description} className="desc" />

              <ul>
                {card.items.map((item, index) => (
                  <li key={index}>
                    <span className="dot"></span>
                    <CmsText content={item} as="span" />
                  </li>
                ))}
              </ul>

              <div className="svcapbutonholder">
                {card.buttonLink ? (
                  <Link to={card.buttonLink} className="learn-btn buttonclip">
                    <CmsText content={card.buttonText} as="span" />
                  </Link>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        <div className="rel-controls">
          <button type="button" onClick={scrollLeft} className="rel-arrow">←</button>
          <button type="button" onClick={scrollRight} className="rel-arrow">→</button>
        </div>
      </div>

      {fields.bottomButtonLink ? (
        <div className="rel-bottom">
          <Link to={fields.bottomButtonLink} className="buttonclip rel-all">
            <CmsText content={fields.bottomButtonText} as="span" />
          </Link>
        </div>
      ) : null}
    </section>
  )
}

export default RelevantServices
